import { useEffect, useState, Fragment, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, to12h } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import TimelineCalendar from "../components/TimelineCalendar";
import NewAppointmentDialog from "../components/NewAppointmentDialog";
import { Plus, Receipt, ChevronDown, ChevronRight, Activity, Bell, CalendarClock, List, X } from "lucide-react";
import { istNow, useIstDay } from "../lib/dates";

const WEEKDAY = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const STATUSES = ["Pending", "Confirmed", "Arrived", "In Service", "Completed", "Cancelled", "No Show"];
const SC = {
  Completed: "text-green-300 border-green-800", Cancelled: "text-red-300 border-red-800",
  "No Show": "text-red-300 border-red-800", Confirmed: "text-primary border-primary",
};
const BILLING_SC = { paid: "text-green-300 border-green-800", due: "text-amber-300 border-amber-700", draft: "text-muted-foreground border-border" };
const TABS = ["All", "Today", "Upcoming", "Completed", "Cancelled", "No Show"];
const dayOffset = (n) => { const d = istNow(); d.setDate(d.getDate() + n); return d; };
const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addMin = (hhmm, m) => { const [h, mm] = (hhmm || "0:0").split(":").map(Number); const t = h * 60 + mm + (Number(m) || 0); return `${String(Math.floor(t / 60) % 24).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`; };

export default function Appointments() {
  const { branches, branchParam } = useBranch();
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [apptInitial, setApptInitial] = useState(null);
  const [filterDate, setFilterDate] = useState(isoDate(dayOffset(0)));
  const [tab, setTab] = useState("Today");
  const [detail, setDetail] = useState(null);
  const [assignSid, setAssignSid] = useState("");
  const [reDate, setReDate] = useState("");
  const [reSlots, setReSlots] = useState([]);
  const [reTime, setReTime] = useState("");
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const canCancel = ["OWNER", "SUPER_ADMIN"].includes(user?.role) || !!(user?.permissions || {}).cancel_appointment;
  const [newCount, setNewCount] = useState(0);
  const [onlyNew, setOnlyNew] = useState(false);
  const [view, setView] = useState("table");
  const loadNewCount = () => api.get("/appointments/new-count", { params: { branch_id: branchParam } }).then((r) => setNewCount(r.data.count)).catch(() => {});
  const openNewBookings = async () => {
    setOnlyNew(true); setTab("All"); setFilterDate("");
    try { await api.post("/appointments/mark-seen", null, { params: { branch_id: branchParam } }); } catch { /* ignore */ }
    setNewCount(0);
  };
  const todayIso = useIstDay();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === "Today") setFilterDate(todayIso); }, [todayIso]); // IST midnight reset
  const pickTab = (t) => { setTab(t); setFilterDate(t === "Today" ? todayIso : ""); };

  const knownApptIds = useRef(null);
  const applyRows = (data, notify) => {
    if (notify && knownApptIds.current) {
      data.forEach((a) => {
        const isNewOnline = !knownApptIds.current.has(a.id) && a.source === "Website" && a.status !== "Cancelled";
        if (isNewOnline) toast.info(`New booking for ${a.staff_name || "staff"}`, { description: `${to12h(a.start_time)} · ${a.service_name || "Service"}` });
      });
    }
    knownApptIds.current = new Set(data.map((a) => a.id));
    setRows(data);
  };
  const load = (notify = false) => api.get("/appointments", { params: { branch_id: branchParam, ...(filterDate ? { date: filterDate } : {}) } }).then((r) => applyRows(r.data, notify));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { knownApptIds.current = null; load(); }, [branchParam, filterDate]);
  useEffect(() => { loadNewCount(); const t = setInterval(loadNewCount, 30000); return () => clearInterval(t); }, [branchParam]);
  // Auto-refresh the Appointment Book (Timeline view) every 2 min so new online bookings
  // appear without a manual reload. Skips when the tab is hidden; does not disturb scroll.
  useEffect(() => {
    if (view !== "timeline") return;
    const t = setInterval(() => { if (!document.hidden) { load(true); loadNewCount(); } }, 120000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, branchParam, filterDate]);
  useEffect(() => { api.get("/staff").then((r) => setStaff(r.data)).catch(() => {}); api.get("/services").then((r) => setServices(r.data)).catch(() => {}); }, []);

  const openNew = () => { setApptInitial({ branch_id: branches[0]?.id || "" }); setOpen(true); };

  const timelineDate = filterDate || todayIso;
  const switchView = (v) => { if (v === "timeline" && !filterDate) setFilterDate(todayIso); setView(v); };
  const branchObj = branches.find((b) => b.id === branchParam);
  const branchHours = { open: branchObj?.opening_time || "09:00", close: branchObj?.closing_time || "21:00" };
  const dayName = (() => { const [y, m, d] = timelineDate.split("-").map(Number); return WEEKDAY[new Date(y, m - 1, d).getDay()]; })();
  const timelineRows = rows.filter((a) => a.branch_id === branchParam && a.date === timelineDate);
  const timelineStaff = (() => {
    const map = new Map();
    staff.filter((s) => (s.status ? s.status !== "inactive" : s.active !== false) && s.branch_id === branchParam)
      .forEach((s) => map.set(s.id, { ...s, weekly_off_today: s.weekly_off === dayName }));
    // Every appointment in the table must render: add rows for staff missing from /staff (other branch, removed, unassigned)
    timelineRows.forEach((a) => {
      const id = a.staff_id || "unassigned";
      if (!map.has(id)) map.set(id, { id, name: a.staff_name || (a.staff_id ? "Staff" : "Unassigned"), branch_id: a.branch_id, extra: true });
    });
    return [...map.values()];
  })();

  // Click a free FUTURE slot in a staff row -> open prefilled Create Appointment
  const bookSlot = (s, time, date) => {
    setApptInitial({ branch_id: branchParam, staff_id: s.id, date, start_time: time });
    setOpen(true);
  };

  // Prefill from Billing/POS timeline: open Add dialog with a chosen slot, or an empty one.
  useEffect(() => {
    const ps = location.state?.prefillSlot;
    const oa = location.state?.openAdd;
    if (ps) {
      setApptInitial({ branch_id: ps.branch_id || branchParam, staff_id: ps.staff_id, date: ps.date, start_time: ps.time });
      setOpen(true);
      window.history.replaceState({}, "");
    } else if (oa) {
      setApptInitial({ branch_id: branchParam });
      setOpen(true);
      window.history.replaceState({}, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Prefill from Service Menu "Book Now" / Enquiry "Book Appointment"
  useEffect(() => {
    const pf = location.state?.prefillService;
    if (!pf) return;
    setApptInitial({
      branch_id: pf.branch_id || branches[0]?.id || "",
      service_id: pf.id || "", staff_id: pf.staff_id || "", duration: pf.duration || 30,
      customer_name: pf.customer_name || "", customer_mobile: pf.customer_mobile || "",
    });
    setOpen(true);
    window.history.replaceState({}, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, branches]);

  const createBill = (a) => {
    const group = a.booking_id ? rows.filter((r) => r.booking_id === a.booking_id) : [a];
    const svcs = group.map((g) => {
      const svc = services.find((s) => s.id === g.service_id);
      return { ...(svc || { id: g.service_id, name: g.service_name, category: "", price: 0, actual_price: 0, duration: g.duration || 30 }), staff_id: g.staff_id || "", staff_name: g.staff_name || "", freedom_group: g.freedom_group || null, freedom_slab_count: g.freedom_slab_count || null, freedom_slab_price: g.freedom_slab_price || null };
    });
    navigate("/billing/new", { state: { prefill: {
      appointmentId: group[0]?.id || a.id,
      appointmentIds: group.map((g) => g.id), booking_id: a.booking_id || null, branch_id: a.branch_id,
      customer_id: a.customer_id || null, customer_name: a.customer_name || "", customer_mobile: a.customer_mobile || "",
      services: svcs,
    } } });
  };

  const setStatus = async (id, status) => {
    try { await api.put(`/appointments/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openManage = (a) => { setDetail(a); setAssignSid(a.staff_id || ""); setReDate(a.date || ""); setReTime(a.start_time || ""); setReSlots([]); };
  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); setDetail(null); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  const doConfirm = (a) => act(() => api.post(`/appointments/${a.id}/confirm`), "Confirmed");
  const doComplete = (a) => act(() => api.post(`/appointments/${a.id}/complete`), "Marked completed");
  const doCancel = (a) => act(() => api.post(`/appointments/${a.id}/cancel`, { reason: `Cancelled by ${user?.role || "staff"}` }), "Appointment cancelled");
  const doAssign = (a) => act(() => api.post(`/appointments/${a.id}/assign-staff`, { staff_id: assignSid, staff_name: staff.find((s) => s.id === assignSid)?.name || "" }), "Staff assigned");
  const doReschedule = (a) => act(() => api.put(`/appointments/${a.id}`, { customer_name: a.customer_name, customer_mobile: a.customer_mobile, branch_id: a.branch_id, service_id: a.service_id, service_name: a.service_name, staff_id: assignSid || a.staff_id, staff_name: staff.find((s) => s.id === (assignSid || a.staff_id))?.name || "", date: reDate, start_time: reTime, duration: a.duration || 30 }), "Rescheduled");

  useEffect(() => {
    if (detail && reDate && (assignSid || detail.staff_id))
      api.get("/appointments/availability/slots", { params: { branch_id: detail.branch_id, staff_id: assignSid || detail.staff_id, date: reDate, duration: detail.duration || 30 } }).then((r) => setReSlots(r.data)).catch(() => setReSlots([]));
  }, [detail, reDate, assignSid]);

  const visibleRows = rows.filter((a) => {
    if (onlyNew && a.source !== "Website") return false;
    if (tab === "Completed") return a.status === "Completed";
    if (tab === "Cancelled") return a.status === "Cancelled";
    if (tab === "No Show") return a.status === "No Show";
    if (tab === "Upcoming") return (a.date >= todayIso) && !["Completed", "Cancelled", "No Show"].includes(a.status);
    return true;
  });

  const [expanded, setExpanded] = useState(new Set());
  const toggleExpand = (k) => setExpanded((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const [showUtil, setShowUtil] = useState(false);
  const [util, setUtil] = useState([]);
  useEffect(() => {
    if (showUtil && branchParam && filterDate)
      api.get("/appointments/utilisation", { params: { branch_id: branchParam, date: filterDate } }).then((r) => setUtil(r.data)).catch(() => setUtil([]));
  }, [showUtil, branchParam, filterDate]);

  const groups = (() => {
    const map = new Map(); const order = [];
    for (const a of visibleRows) {
      const key = a.booking_id || `single-${a.id}`;
      if (!map.has(key)) { map.set(key, []); order.push(key); }
      map.get(key).push(a);
    }
    return order.map((k) => ({ key: k, items: map.get(k) }));
  })();

  return (
    <div>
      <PageHeader title="Appointments" subtitle="Bookings & calendar"
        actions={
          <div className="flex items-center gap-2">
            <Input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} data-testid="appt-filter-date" className="h-9 w-40 bg-secondary" />
            {filterDate && <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => setFilterDate("")} data-testid="appt-filter-clear">All dates</Button>}
            <Button onClick={openNew} data-testid="add-appointment-btn" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]"><Plus size={16} className="mr-1" /> Add Appointment</Button>
          </div>
        } />

      {newCount > 0 && (
        <button onClick={openNewBookings} data-testid="new-appt-notification"
          className="w-full mb-4 flex items-center gap-3 rounded-sm border border-primary/50 bg-primary/10 px-4 py-3 text-left hover:bg-primary/20 transition-colors">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary"></span>
          </span>
          <Bell size={16} className="text-primary" />
          <span className="text-sm font-medium text-primary">{newCount} New Appointment{newCount > 1 ? "s" : ""}</span>
          <span className="text-xs text-muted-foreground ml-1">— click to view online bookings</span>
        </button>
      )}
      {onlyNew && (
        <div className="mb-3 flex items-center gap-2 text-xs text-muted-foreground" data-testid="new-appt-viewing">
          Showing online bookings only
          <Button variant="ghost" size="sm" className="h-6 px-2 rounded-sm" onClick={() => setOnlyNew(false)} data-testid="new-appt-clear">Show all</Button>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4" data-testid="appt-tabs">
        {TABS.map((t) => (
          <Button key={t} type="button" variant="ghost" onClick={() => pickTab(t)}
            data-testid={`appt-tab-${t.replace(/\s/g, "-")}`}
            className={`rounded-sm px-4 h-9 ${tab === t ? "bg-primary text-primary-foreground hover:bg-[#D4AF37]" : "bg-secondary text-foreground"}`}>{t}</Button>
        ))}
      </div>

      <div className="mb-4">
        <button onClick={() => setShowUtil((v) => !v)} data-testid="toggle-utilisation"
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          {showUtil ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <Activity size={15} className="text-primary" /> Staff Utilisation{filterDate ? ` · ${filterDate}` : ""}
        </button>
        {showUtil && (
          <Card className="p-4 bg-card border-border mt-2" data-testid="utilisation-panel">
            {!branchParam ? <p className="text-sm text-muted-foreground">Select a single branch (top-left) to see stylist utilisation.</p>
              : !util.length ? <p className="text-sm text-muted-foreground">No active staff for this branch.</p>
                : <div className="space-y-3">
                    {util.map((u) => {
                      const pct = u.working_minutes ? Math.min(100, Math.round(u.booked_minutes / u.working_minutes * 100)) : 0;
                      return (
                        <div key={u.staff_id} data-testid={`util-${u.staff_id}`}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="font-medium">{u.name}{u.weekly_off && <span className="ml-2 text-xs text-muted-foreground">(weekly off)</span>}</span>
                            <span className="text-muted-foreground">{u.booked_minutes}/{u.working_minutes} min · <span className="text-primary">{u.free_minutes} min free</span></span>
                          </div>
                          <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-primary" style={{ width: `${pct}%` }} /></div>
                        </div>
                      );
                    })}
                  </div>}
          </Card>
        )}
      </div>

      <div className="flex items-center gap-2 mb-4" data-testid="appt-view-toggle">
        <Button variant="ghost" onClick={() => switchView("table")} data-testid="view-table" className={`rounded-sm h-9 ${view === "table" ? "bg-primary text-primary-foreground hover:bg-[#D4AF37]" : "bg-secondary"}`}><List size={15} className="mr-1" />Table</Button>
        <Button variant="ghost" onClick={() => switchView("timeline")} data-testid="view-timeline" className={`rounded-sm h-9 ${view === "timeline" ? "bg-primary text-primary-foreground hover:bg-[#D4AF37]" : "bg-secondary"}`}><CalendarClock size={15} className="mr-1" />Timeline</Button>
        {view === "timeline" && <span className="text-xs text-muted-foreground ml-2">{branchObj?.name || "Branch-wise"} · {timelineDate}</span>}
      </div>

      {view === "timeline" && (
        !branchParam
          ? <Card className="p-8 bg-card border-border text-center text-muted-foreground" data-testid="timeline-pick-branch">Select a single branch (top-left) to view its timeline. The timeline is branch-wise for operational use.</Card>
          : <TimelineCalendar branchId={branchParam} date={timelineDate} staff={timelineStaff}
              appointments={timelineRows}
              branchHours={branchHours} onSlotClick={bookSlot} />
      )}

      {view === "table" && (
      <Card className="bg-card border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Customer</TableHead><TableHead>Service</TableHead><TableHead>Staff</TableHead>
              <TableHead>Date</TableHead><TableHead>Time</TableHead><TableHead>Source</TableHead><TableHead>Billing</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.map((g) => {
              const its = [...g.items].sort((a, b) => (a.booking_order ?? 0) - (b.booking_order ?? 0) || (a.start_time || "").localeCompare(b.start_time || ""));
              if (its.length === 1) {
                const a = its[0];
                return (
                  <TableRow key={a.id} className="border-border" data-testid={`appt-row-${a.id}`}>
                    <TableCell className="font-medium">{a.customer_name}<div className="text-xs text-muted-foreground">{a.customer_mobile}</div></TableCell>
                    <TableCell>{a.service_name}</TableCell>
                    <TableCell>{a.staff_name}</TableCell>
                    <TableCell>{a.date}</TableCell>
                    <TableCell>{to12h(a.start_time)}–{to12h(a.end_time)}</TableCell>
                    <TableCell><span className="text-xs">{a.source || "—"}</span>{a.source === "Website" && <Badge className="ml-1 bg-primary/20 text-primary text-[10px] px-1.5">ONLINE</Badge>}</TableCell>
                    <TableCell>{a.billing_status ? <div><Badge variant="outline" className={BILLING_SC[a.billing_status] || ""}>{a.billing_status}</Badge>{a.bill_no && <div className="text-[11px] text-muted-foreground mt-0.5" data-testid={`appt-billno-${a.id}`}>{a.bill_no}{a.payment_mode ? ` · ${a.payment_mode}` : ""}</div>}</div> : <span className="text-xs text-muted-foreground">Not billed</span>}</TableCell>
                    <TableCell><Badge variant="outline" className={SC[a.status] || ""}>{a.status}</Badge></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost" className="h-8 rounded-sm" onClick={() => openManage(a)} data-testid={`appt-manage-${a.id}`}>Manage</Button>
                        <Select value={a.status} onValueChange={(v) => setStatus(a.id, v)}>
                          <SelectTrigger className="h-8 w-32 bg-secondary" data-testid={`appt-status-${a.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                        {!["Cancelled", "No Show"].includes(a.status) && (
                          <Button size="sm" variant="outline" className="h-8 rounded-sm border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                            onClick={() => createBill(a)} data-testid={`appt-create-bill-${a.id}`}>
                            <Receipt size={14} className="mr-1" /> Create Bill
                          </Button>
                        )}
                        {canCancel && !["Cancelled", "No Show", "Completed"].includes(a.status) && (
                          <Button size="sm" variant="outline" className="h-8 rounded-sm border-destructive/50 text-destructive hover:bg-destructive hover:text-white"
                            onClick={() => doCancel(a)} data-testid={`appt-cancel-${a.id}`}>
                            <X size={14} className="mr-1" /> Cancel
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              }
              const first = its[0], last = its[its.length - 1];
              const isOpen = expanded.has(g.key);
              return (
                <Fragment key={g.key}>
                  <TableRow className="border-border bg-secondary/30" data-testid={`appt-booking-${g.key}`}>
                    <TableCell className="font-medium">
                      <button className="flex items-center gap-1" onClick={() => toggleExpand(g.key)} data-testid={`appt-booking-toggle-${g.key}`}>
                        {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}<span>{first.customer_name}</span>
                      </button>
                      <div className="text-xs text-muted-foreground pl-5">{first.customer_mobile}</div>
                    </TableCell>
                    <TableCell>{its.length} services <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-sm bg-primary/15 text-primary">Multi</span></TableCell>
                    <TableCell className="text-muted-foreground text-xs">{its.map((x) => x.staff_name).filter(Boolean).join(", ")}</TableCell>
                    <TableCell>{first.date}</TableCell>
                    <TableCell>{to12h(first.start_time)}–{to12h(last.end_time)}</TableCell>
                    <TableCell><span className="text-xs">{first.source || "—"}</span></TableCell>
                    <TableCell>{first.billing_status ? <div><Badge variant="outline" className={BILLING_SC[first.billing_status] || ""}>{first.billing_status}</Badge>{first.bill_no && <div className="text-[11px] text-muted-foreground mt-0.5" data-testid={`appt-billno-${first.id}`}>{first.bill_no}{first.payment_mode ? ` · ${first.payment_mode}` : ""}</div>}</div> : <span className="text-xs text-muted-foreground">Not billed</span>}</TableCell>
                    <TableCell><Badge variant="outline">{its.length}×</Badge></TableCell>
                    <TableCell>
                      {!its.every((x) => ["Cancelled", "No Show"].includes(x.status)) && (
                        <Button size="sm" variant="outline" className="h-8 rounded-sm border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                          onClick={() => createBill(first)} data-testid={`appt-create-bill-${first.id}`}>
                          <Receipt size={14} className="mr-1" /> Create Bill
                        </Button>
                      )}
                      {canCancel && !its.every((x) => ["Cancelled", "No Show"].includes(x.status)) && (
                        <Button size="sm" variant="outline" className="h-8 rounded-sm ml-2 border-destructive/50 text-destructive hover:bg-destructive hover:text-white"
                          onClick={() => doCancel(first)} data-testid={`appt-cancel-${first.id}`}>
                          <X size={14} className="mr-1" /> Cancel
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  {isOpen && its.map((a) => (
                    <TableRow key={a.id} className="border-border" data-testid={`appt-row-${a.id}`}>
                      <TableCell></TableCell>
                      <TableCell className="pl-6">{a.service_name}</TableCell>
                      <TableCell>{a.staff_name}</TableCell>
                      <TableCell>{a.date}</TableCell>
                      <TableCell>{to12h(a.start_time)}–{to12h(a.end_time)}</TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell><Badge variant="outline" className={SC[a.status] || ""}>{a.status}</Badge></TableCell>
                      <TableCell>
                        <Select value={a.status} onValueChange={(v) => setStatus(a.id, v)}>
                          <SelectTrigger className="h-8 w-32 bg-secondary" data-testid={`appt-status-${a.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </Fragment>
              );
            })}
            {!visibleRows.length && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-10">No appointments.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
      )}

      <NewAppointmentDialog open={open} onOpenChange={setOpen} branches={branches} services={services} staff={staff}
        initial={apptInitial} onSaved={() => load()} />

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto" data-testid="appt-manage-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Manage Appointment</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="text-sm space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span>{detail.customer_name} · {detail.customer_mobile}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Service</span><span>{detail.service_name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">When</span><span>{detail.date} {to12h(detail.start_time)}–{to12h(detail.end_time)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Source</span><span>{detail.source || "—"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Status</span><Badge variant="outline" className={SC[detail.status] || ""}>{detail.status}</Badge></div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" disabled={busy} onClick={() => doConfirm(detail)} data-testid="manage-confirm">Confirm</Button>
                <Button size="sm" variant="outline" className="rounded-sm" disabled={busy} onClick={() => doComplete(detail)} data-testid="manage-complete">Mark Completed</Button>
                <Button size="sm" variant="outline" className="rounded-sm text-destructive" disabled={busy} onClick={() => { const d = detail; setDetail(null); doCancel(d); }} data-testid="manage-cancel">Cancel</Button>
                <Button size="sm" variant="outline" className="rounded-sm border-primary text-primary" disabled={busy} onClick={() => { const d = detail; setDetail(null); createBill(d); }} data-testid="manage-create-bill"><Receipt size={14} className="mr-1" />Create Bill</Button>
              </div>
              <div className="border-t border-border pt-3 space-y-2">
                <Label className="text-xs">Assign Staff</Label>
                <div className="flex gap-2">
                  <Select value={assignSid} onValueChange={setAssignSid}>
                    <SelectTrigger className="bg-secondary" data-testid="manage-staff-select"><SelectValue placeholder="Staff" /></SelectTrigger>
                    <SelectContent>{staff.filter((s) => s.branch_id === detail.branch_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" className="rounded-sm" disabled={busy || !assignSid} onClick={() => doAssign(detail)} data-testid="manage-assign-save">Save</Button>
                </div>
              </div>
              <div className="border-t border-border pt-3 space-y-2">
                <Label className="text-xs">Reschedule</Label>
                <Input type="date" value={reDate} onChange={(e) => setReDate(e.target.value)} className="bg-secondary" data-testid="manage-reschedule-date" />
                <div className="grid grid-cols-4 gap-2 max-h-32 overflow-y-auto">
                  {reSlots.map((s) => (
                    <Button key={s.time} type="button" size="sm" variant="ghost" disabled={!s.available}
                      onClick={() => setReTime(s.time)} data-testid={`manage-slot-${s.time}`}
                      className={`rounded-sm h-8 ${reTime === s.time ? "bg-primary text-primary-foreground" : "bg-secondary"} ${!s.available ? "opacity-30" : ""}`}>{to12h(s.time)}</Button>
                  ))}
                </div>
                <Button size="sm" className="rounded-sm" disabled={busy || !reDate || !reTime} onClick={() => doReschedule(detail)} data-testid="manage-reschedule-save">Reschedule</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
